import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  Tag, 
  Trash2, 
  Plus, 
  X, 
  Wand2,
  Share2,
  FolderOpen,
  ArrowLeft,
  Check,
  Settings,
  Key,
  Lock,
  Unlock,
  Image
} from 'lucide-react';
import { useNoteStore } from '../stores/noteStore';
import { useSettingsStore, fontFamilyMap } from '../stores/settingsStore';
import { useNavigate } from 'react-router-dom';
import ChecklistEditor from './ChecklistEditor';
import PasswordEditor from './PasswordEditor';
import TagInput from './TagInput';
import SettingsPanel from './SettingsPanel';
import PinLockScreen from './PinLockScreen';
import ImageGallery from './ImageGallery';
import { NoteImage } from '../types';

const NoteEditor: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentNote,
    folders,
    updateNote,
    deleteNote,
    addTag,
    removeTag,
  } = useNoteStore();

  const { 
    paperStyle, 
    textPaddingTop, 
    fontSize, 
    fontFamily, 
    lineOpacity, 
    marginLineOpacity,
    lineSpacing,
    pinCode,
    isPinSessionValid,
    startPinSession,
  } = useSettingsStore();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  // Auto-save functionality
  useEffect(() => {
    if (!currentNote) return;

    const saveTimer = setTimeout(() => {
      if (title !== currentNote.title || content !== currentNote.content) {
        updateNote(currentNote.id, { title, content });
      }
    }, 800);

    return () => clearTimeout(saveTimer);
  }, [title, content, currentNote, updateNote]);

  // Load note data when current note changes
  useEffect(() => {
    if (currentNote) {
      setTitle(currentNote.title);
      setContent(currentNote.content);
      
      // Auto-focus title if it's empty (new note)
      if (!currentNote.title && titleRef.current) {
        setTimeout(() => titleRef.current?.focus(), 100);
      }
    } else {
      setTitle('');
      setContent('');
    }
  }, [currentNote?.id]); // Only depend on note ID change

  const handleTogglePinned = async () => {
    if (!currentNote) return;
    await updateNote(currentNote.id, { isPinned: !currentNote.isPinned });
  };

  const handleToggleLocked = async () => {
    if (!currentNote) return;
    if (!pinCode) {
      alert('Please set up a PIN in Settings first');
      return;
    }
    await updateNote(currentNote.id, { isLocked: !currentNote.isLocked });
  };

  const handleImagesChange = async (images: NoteImage[]) => {
    if (!currentNote) return;
    await updateNote(currentNote.id, { images });
  };

  const handleDeleteNote = async () => {
    if (!currentNote) return;
    if (window.confirm('Are you sure you want to delete this note?')) {
      await deleteNote(currentNote.id);
      navigate('/');
    }
  };

  const handleAddTag = async (tagName: string) => {
    if (!currentNote) return;
    await addTag(currentNote.id, tagName);
    setShowTagInput(false);
  };

  const handleRemoveTag = async (tagName: string) => {
    if (!currentNote) return;
    await removeTag(currentNote.id, tagName);
  };

  const handleMoveToFolder = async (folderId: string | undefined) => {
    if (!currentNote) return;
    await updateNote(currentNote.id, { folderId });
    setShowFolderPicker(false);
  };

  const handleShare = async () => {
    if (!currentNote) return;
    
    // Build share content
    let shareText = '';
    if (currentNote.type === 'checklist' && currentNote.checklistItems) {
      shareText = currentNote.checklistItems
        .map(item => `${item.completed ? '✓' : '○'} ${item.text}`)
        .join('\n');
    } else if (currentNote.type === 'password') {
      // Don't share password content for security
      shareText = `Password entry: ${currentNote.title}`;
    } else {
      shareText = currentNote.content;
    }

    const shareData = {
      title: currentNote.title || 'Untitled Note',
      text: shareText,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}`);
        alert('Copied to clipboard!');
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };  const currentFolderName = currentNote?.folderId 
    ? folders.find(f => f.id === currentNote.folderId)?.name 
    : 'All Notes';

  if (!currentNote) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex-1 flex items-center justify-center bg-gradient-to-br from-yellow-100 to-yellow-50"
      >
        <div className="text-center p-8">
          <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-2xl shadow-lg flex items-center justify-center">
            <Star className="w-10 h-10 text-yellow-400" />
          </div>
          <h3 className="text-xl font-semibold text-notepad-text mb-2">Select a note</h3>
          <p className="text-gray-500">Choose a note from the list or create a new one</p>
        </div>
      </motion.div>
    );
  }

  // Check if note is locked and needs PIN (skip if PIN session is valid)
  const needsPinEntry = currentNote.isLocked && !isPinSessionValid();

  // Show PIN prompt immediately for locked notes
  if (needsPinEntry) {
    return (
      <PinLockScreen
        mode="unlock"
        onSuccess={() => {
          // Start a PIN session so user doesn't have to re-enter for other locked notes
          startPinSession();
        }}
        onCancel={() => {
          navigate('/');
        }}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex-1 flex flex-col h-full overflow-hidden"
    >
      {/* Mobile Header with Back Button */}
      <div className="flex items-center gap-2 p-3 bg-yellow-100 border-b border-yellow-200 md:hidden">
        <button
          onClick={() => navigate('/')}
          className="p-2 hover:bg-yellow-200 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <span className="text-sm text-gray-600 flex-1 truncate">{currentFolderName}</span>
        <button
          onClick={handleTogglePinned}
          className={`p-2 rounded-xl transition-colors ${currentNote.isPinned ? 'text-yellow-600 bg-yellow-200' : 'text-gray-500'}`}
        >
          <Star className={`w-5 h-5 ${currentNote.isPinned ? 'fill-current' : ''}`} />
        </button>
        <button
          onClick={() => setShowActions(!showActions)}
          className="p-2 hover:bg-yellow-200 rounded-xl transition-colors"
        >
          <Plus className={`w-5 h-5 text-gray-700 transition-transform ${showActions ? 'rotate-45' : ''}`} />
        </button>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:flex items-center justify-between p-3 bg-yellow-100 border-b border-yellow-200">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFolderPicker(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-yellow-200 hover:bg-yellow-300 rounded-lg text-sm text-gray-700 transition-colors"
          >
            <FolderOpen className="w-4 h-4" />
            {currentFolderName}
          </button>
          
          {/* Tags */}
          {currentNote.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 bg-white/50 text-gray-700 rounded-lg text-sm"
            >
              <Tag className="w-3 h-3" />
              {tag}
              <button
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 hover:bg-white/50 rounded p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          
          {showTagInput ? (
            <TagInput
              onAdd={handleAddTag}
              onCancel={() => setShowTagInput(false)}
            />
          ) : (
            <button
              onClick={() => setShowTagInput(true)}
              className="flex items-center gap-1 px-2 py-1 text-gray-500 hover:bg-yellow-200 rounded-lg text-sm transition-colors"
            >
              <Plus className="w-3 h-3" />
              Tag
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={handleToggleLocked}
            className={`p-2 rounded-xl transition-colors ${
              currentNote.isLocked === true
                ? 'bg-yellow-300 text-yellow-700' 
                : 'hover:bg-yellow-200 text-gray-600'
            }`}
            title={currentNote.isLocked ? 'Unlock note' : 'Lock note with PIN'}
          >
            {currentNote.isLocked === true ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
          </button>
          <button
            onClick={handleTogglePinned}
            className={`p-2 rounded-xl transition-colors ${
              currentNote.isPinned 
                ? 'bg-yellow-300 text-yellow-700' 
                : 'hover:bg-yellow-200 text-gray-600'
            }`}
            title="Pin note"
          >
            <Star className={`w-5 h-5 ${currentNote.isPinned ? 'fill-current' : ''}`} />
          </button>
          <button
            onClick={handleShare}
            className="p-2 hover:bg-yellow-200 rounded-xl text-gray-600"
            title="Share note"
          >
            <Share2 className="w-5 h-5" />
          </button>
          <button className="p-2 hover:bg-yellow-200 rounded-xl text-gray-600" title="AI assist">
            <Wand2 className="w-5 h-5" />
          </button>
          <button
            onClick={handleDeleteNote}
            className="p-2 hover:bg-red-100 rounded-xl text-red-500"
            title="Delete note"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile Actions Dropdown */}
      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-yellow-50 border-b border-yellow-200 overflow-hidden"
          >
            <div className="p-3 flex flex-wrap gap-2">
              <button
                onClick={() => { setShowFolderPicker(true); setShowActions(false); }}
                className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm text-gray-700"
              >
                <FolderOpen className="w-4 h-4" />
                Move
              </button>
              <button
                onClick={() => { setShowTagInput(true); setShowActions(false); }}
                className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm text-gray-700"
              >
                <Tag className="w-4 h-4" />
                Add Tag
              </button>
              <button
                onClick={() => { handleToggleLocked(); setShowActions(false); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  currentNote.isLocked ? 'bg-yellow-200 text-yellow-700' : 'bg-white text-gray-700'
                }`}
              >
                {currentNote.isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                {currentNote.isLocked ? 'Locked' : 'Lock'}
              </button>
              <button className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm text-gray-700">
                <Wand2 className="w-4 h-4" />
                AI
              </button>
              <button 
                onClick={() => { handleShare(); setShowActions(false); }}
                className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm text-gray-700"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              <button
                onClick={handleDeleteNote}
                className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg text-sm text-red-600"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
            
            {/* Tags display */}
            {currentNote.tags.length > 0 && (
              <div className="px-3 pb-3 flex flex-wrap gap-2">
                {currentNote.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-200 text-gray-700 rounded-full text-sm"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:bg-yellow-300 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Tag Input */}
      {showTagInput && (
        <div className="md:hidden p-3 bg-yellow-50 border-b border-yellow-200">
          <TagInput
            onAdd={handleAddTag}
            onCancel={() => setShowTagInput(false)}
          />
        </div>
      )}

      {/* ========================================
          NOTEPAD PAPER - The main writing area
          ======================================== */}
      <div className="flex-1 overflow-hidden">
        {(() => {
          // Calculate line colors with opacity
          const opacityToHex = (opacity: number) => {
            const alpha = Math.round((opacity / 100) * 255);
            return alpha.toString(16).padStart(2, '0');
          };
          const lineColor = `#6B7280${opacityToHex(lineOpacity)}`;
          const marginColor = `#DC2626${opacityToHex(marginLineOpacity)}`;
          
          const paperBackground = paperStyle === 'lined' 
            ? {
                backgroundColor: '#FEF9C3',
                backgroundImage: `linear-gradient(to right, transparent 0, transparent 48px, ${marginColor} 48px, ${marginColor} 50px, transparent 50px),
                  repeating-linear-gradient(to bottom, transparent 0px, transparent ${lineSpacing - 1}px, ${lineColor} ${lineSpacing - 1}px, ${lineColor} ${lineSpacing}px)`,
                backgroundSize: `100% 100%, 100% ${lineSpacing}px`,
                backgroundAttachment: 'local' as const,
              }
            : { backgroundColor: '#FEF9C3' };

          // Password note type
          if (currentNote.type === 'password') {
            return (
              <div className="h-full overflow-y-auto bg-gray-50">
                {/* Password Header */}
                <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Key className="w-6 h-6 text-white" />
                    </div>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Account name..."
                      className="flex-1 bg-transparent text-white text-xl font-semibold placeholder-white/70 outline-none"
                    />
                  </div>
                </div>
                <PasswordEditor note={currentNote} />
              </div>
            );
          }

          return currentNote.type === 'checklist' ? (
            <div 
              className="h-full overflow-y-auto"
              style={paperBackground}
            >
              {/* Title on notepad - sticky */}
              <div 
                className="sticky top-0 z-10"
                style={{
                  backgroundColor: paperStyle === 'lined' ? '#FEF9C3' : '#FEF9C3',
                }}
              >
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Checklist title..."
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    fontFamily: fontFamilyMap[fontFamily],
                    fontSize: `${fontSize + 4}px`,
                    fontWeight: 600,
                    lineHeight: `${lineSpacing}px`,
                    color: '#1F2937',
                    padding: paperStyle === 'lined' ? `${textPaddingTop}px 16px 0 60px` : '16px 16px 0 16px',
                  }}
                />
              </div>
              <ChecklistEditor note={currentNote} />
              
              {/* Image Gallery */}
              <div 
                className="border-t border-gray-200/50 bg-white/30 mt-4"
                style={{ padding: paperStyle === 'lined' ? '16px 16px 16px 60px' : '16px' }}
              >
                <ImageGallery
                  noteId={currentNote.id}
                  images={currentNote.images || []}
                  onImagesChange={handleImagesChange}
                />
              </div>
            </div>
          ) : (
            <div 
              className="h-full overflow-y-auto"
              style={paperBackground}
            >
              {/* Title input - sticky at top */}
              <div 
                className="sticky top-0 z-10"
                style={{
                  backgroundColor: paperStyle === 'lined' ? '#FEF9C3' : '#FEF9C3',
                }}
              >
                <input
                  ref={titleRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Note title..."
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    fontFamily: fontFamilyMap[fontFamily],
                    fontSize: `${fontSize + 4}px`,
                    fontWeight: 600,
                    lineHeight: `${lineSpacing}px`,
                    color: '#1F2937',
                    padding: paperStyle === 'lined' ? `${textPaddingTop}px 16px 0 60px` : '16px 16px 0 16px',
                  }}
                />
              </div>
              
              {/* Content textarea - text sits on the lines */}
              <textarea
                ref={contentRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Start writing..."
                className="min-h-[400px]"
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  width: '100%',
                  fontFamily: fontFamilyMap[fontFamily],
                  fontSize: `${fontSize}px`,
                  lineHeight: `${lineSpacing}px`,
                  color: '#1F2937',
                  padding: paperStyle === 'lined' ? `${textPaddingTop}px 16px 16px 60px` : '0 16px 16px 16px',
                }}
              />
              
              {/* Image Gallery */}
              <div 
                className="border-t border-gray-200/50 bg-white/30"
                style={{ padding: paperStyle === 'lined' ? '16px 16px 16px 60px' : '16px' }}
              >
                <ImageGallery
                  noteId={currentNote.id}
                  images={currentNote.images || []}
                  onImagesChange={handleImagesChange}
                />
              </div>
            </div>
          );
        })()}
      </div>

      {/* Status Bar */}
      <div className="px-4 py-2 bg-yellow-100 border-t border-yellow-200 text-xs text-gray-500 flex items-center justify-between">
        <button
          onClick={() => setShowSettings(true)}
          className="flex items-center gap-1 hover:text-gray-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" />
          <span>Settings</span>
        </button>
        <div className="flex items-center gap-3">
          {(currentNote.images?.length || 0) > 0 && (
            <span className="flex items-center gap-1">
              <Image className="w-3.5 h-3.5" />
              {currentNote.images?.length}
            </span>
          )}
          <span>
            {currentNote.type === 'checklist' 
              ? `${currentNote.checklistItems?.filter(i => i.completed).length || 0}/${currentNote.checklistItems?.length || 0} done`
              : currentNote.type === 'password'
              ? '🔒 Secure'
              : `${content.length} chars`
            }
          </span>
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
      </AnimatePresence>

      {/* Folder Picker Modal */}
      <AnimatePresence>
        {showFolderPicker && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowFolderPicker(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[70vh] overflow-hidden"
            >
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-notepad-text">Move to Folder</h3>
                  <button
                    onClick={() => setShowFolderPicker(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[50vh] p-2">
                {/* All Notes option */}
                <button
                  onClick={() => handleMoveToFolder(undefined)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    !currentNote.folderId ? 'bg-yellow-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-10 h-10 bg-yellow-200 rounded-xl flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-yellow-700" />
                  </div>
                  <span className="flex-1 text-left font-medium">All Notes</span>
                  {!currentNote.folderId && <Check className="w-5 h-5 text-yellow-600" />}
                </button>

                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => handleMoveToFolder(folder.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                      currentNote.folderId === folder.id ? 'bg-yellow-100' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: folder.color }}
                    >
                      <FolderOpen className="w-5 h-5 text-gray-600" />
                    </div>
                    <span className="flex-1 text-left font-medium">{folder.name}</span>
                    {currentNote.folderId === folder.id && <Check className="w-5 h-5 text-yellow-600" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default NoteEditor;
